import * as React from "react";
import { useForm } from "react-hook-form";
import { Vector3 } from "three";

import "./index.css";
import Geometry from "./three/Geometry";
import PointForm from "./components/pointForm";
import LineForm from "./components/lineForm";
import { type Point, type Line } from "./types";

type IntersectForm = {
  lineAId: string,
  lineBId: string
};

const defaultPoints: [Point, Point, Point, Point] = [
  {
    id: self.crypto.randomUUID(),
    name: "A",
    x: 1,
    y: -2,
    z: -3
  },
  {
    id: self.crypto.randomUUID(),
    name: "B",
    x: 3,
    y: 1,
    z: 2
  },
  {
    id: self.crypto.randomUUID(),
    name: "C",
    x: 1,
    y: -3,
    z: -2
  },
  {
    id: self.crypto.randomUUID(),
    name: "D",
    x: -2,
    y: -7,
    z: -10
  },
];

const defaultLines: Line[] = [
  {
    id: self.crypto.randomUUID(),
    pointA: defaultPoints[0],
    pointB: defaultPoints[1]
  },
  {
    id: self.crypto.randomUUID(),
    pointA: defaultPoints[2],
    pointB: defaultPoints[3]
  }
];
export function App() {
  const [points, setPoints] = React.useState<Point[]>(defaultPoints);
  const [lines, setLines] = React.useState<Line[]>(defaultLines);
  const [intersectionPoint, setIntersectionPoint] = React.useState<Vector3 | null>(null);
  const [lineIntersectStatus, setLineIntersectStatus] = React.useState("Ready to check an intersection.");
  const canvasContainerRef = React.useRef<HTMLDivElement>(null);
  const geometry = React.useRef<Geometry | null>(null);
  const { clearErrors, handleSubmit, register, setError, formState } = useForm<IntersectForm>();

  React.useEffect(() => {
    if (!canvasContainerRef.current) return;

    const nextGeometry = new Geometry(canvasContainerRef.current);
    geometry.current = nextGeometry;
    let cancelled = false;

    const init = async () => {
      await nextGeometry.init();
      if (cancelled) return;

      points.forEach(p => nextGeometry.addPoint(p));
      lines.forEach(l => nextGeometry.addLine(l));
      nextGeometry.render();
    }

    init();

    return () => {
      cancelled = true;
      nextGeometry.dispose();
      if (geometry.current === nextGeometry) {
        geometry.current = null;
      }
    };
  }, []);

  const onSubmitPoint = (point: Point) => {
    setPoints(prev => [...prev, point]);
    if (geometry.current) {
      geometry.current.addPoint(point);
      geometry.current.renderPoints();
    }
  };

  const onSubmitLine = (line: Line) => {
    setLines(prev => [...prev, line]);
    if (geometry.current) {
      geometry.current.addLine(line);
      geometry.current.renderLines();
    }
  };

  const removePoint = (id: string) => {
    setPoints(prev => prev.filter(p => p.id !== id));

    setLines(currentLines => {
      const remainingLines = currentLines.filter(l => {
        const shouldRemove = l.pointA.id === id || l.pointB.id === id;
        if (shouldRemove) {
          geometry.current?.removeLine(l.id);
        }

        return !shouldRemove;
      });

      return remainingLines;
    });
    
    if (geometry.current) {
      geometry.current.removePoint(id);
    }
  }

  const removeLine = (id: string) => {
    setLines(currentLines => currentLines.filter(l => l.id !== id));
    
    if (geometry.current) {
      geometry.current.removeLine(id);
    }
  }
  

  const onLineIntersectSubmit = (data: IntersectForm) => {
    if (!geometry.current) return;
    
    if (data.lineAId === data.lineBId) {
      setIntersectionPoint(null);
      setLineIntersectStatus("Choose two different lines.");
      setError("lineAId", { type: "validate" });
      setError("lineBId", { type: "validate" });
      return;
    }

    const line1 = geometry.current.getLine(data.lineAId);
    const line2 = geometry.current.getLine(data.lineBId);

    if (!line1 || !line2) {
      setIntersectionPoint(null);
      setLineIntersectStatus("Select two existing lines.");
      setError("lineAId", { type: "validate" });
      setError("lineBId", { type: "validate" });
      return;
    }

    clearErrors();
    const ip = line1.intersect(line2);

    if (ip) {
      setIntersectionPoint(ip);
      setLineIntersectStatus(`Intersection point: (${ip.x};${ip.y};${ip.z}).`);
    } else {
      setIntersectionPoint(null);
      setLineIntersectStatus("The two lines don't intersect.");
    }
  }

  const onLineIntersectInvalid = () => {
    setIntersectionPoint(null);
    setLineIntersectStatus("Select two lines to check.");
  };

  const goToPoint = (p: Vector3) => {
    if (!geometry.current) return;

    geometry.current.goToPoint(p);
  };

  return (
    <div className="app">
      <div ref={canvasContainerRef} className="canvas-container" />
      <PointForm points={points} onSubmit={onSubmitPoint} onRemove={removePoint} />
      <LineForm points={points} lines={lines} onSubmit={onSubmitLine} onRemove={removeLine} />
      <div className="line-intersect">
        { lines.length < 2 && (
          <p>Add lines to check if they intersect.</p>
        )}
        {lines.length >= 2 && (
          <>
            <form onSubmit={handleSubmit(onLineIntersectSubmit, onLineIntersectInvalid)}>
                <p className="form-status" role="status">{lineIntersectStatus}</p>
                Does line 
                <select
                  aria-invalid={Boolean(formState.errors.lineAId)}
                  className={formState.errors.lineAId ? "is-invalid" : undefined}
                  {...register("lineAId", { required: true })}
                >
                  { lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.pointA.name}{l.pointB.name}
                    </option>
                  ))}
                </select>
                and line
                <select
                  aria-invalid={Boolean(formState.errors.lineBId)}
                  className={formState.errors.lineBId ? "is-invalid" : undefined}
                  {...register("lineBId", { required: true })}
                >
                  { lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.pointA.name}{l.pointB.name}
                    </option>
                  ))}
                </select>
                intersect ?
              <input type="submit" value="Check" />
            </form>
            { formState.isSubmitted && intersectionPoint && (
              <p>
                <button onClick={() => goToPoint(intersectionPoint)}>Go to point</button>
              </p>
            )}
            { formState.isSubmitted && (
              <button onClick={() => goToPoint(new Vector3(0,0,0))}>Reset camera</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
